import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { environment } from '@env';
import { FileUpload } from '@material';
import { translate, TRANSLOCO_SCOPE } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { UtilsService } from '../../services/utils/utils.service';

import { AssetData } from '../../models/assetdata.class';
import { Transformation } from '../../models/transformation';
import { AlertError, AlertSuccess, ClearDownloadableFiles, GetInstanceTransformations } from '../../store/actions';
import { getInstanceTransformations$ } from '../../store/selectors';

@Component({
    selector: 'app-upload-files-dialog',
    templateUrl: './upload-files-dialog.component.html',
    styleUrls: ['./upload-files-dialog.component.scss'],
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: '',
        },
    ],
    standalone: false,
})
export class UploadFilesDialogComponent implements OnDestroy, OnInit {
    @Input() public record: any;
    @Input() public metaData: AssetData;

    private unSubscribe = new Subject<void>();
    public transformations: {
        orgName: string;
        transformations: Transformation[];
        repositoryId: string;
    }[] = [];
    private assetToAssetTransformations: {
        orgName: string;
        transformations: Transformation[];
    }[] = [];
    public selectedTransformationName: string;
    private selectedTransformation: Transformation;
    private transformationSources = [];
    public transformationErrors = [];
    public sourceTableData = new BehaviorSubject([]);
    public sourceTableColumns = [
        {
            name: 'name',
            displayName: translate('Source Name'),
            show: true,
            type: 'text',
        },
        {
            name: 'sourceDataFormat',
            displayName: translate('Supported Format'),
            show: true,
            type: 'text',
        },
        {
            name: 'from',
            displayName: translate('Choose File'),
            show: true,
            type: 'file',
        },
        {
            name: 'downloadSourceTemplate',
            displayName: translate('Download Source Template'),
            show: true,
            type: 'link',
        },
    ];
    public hidePaginator = true;
    public primaryColumns = ['Source Name', 'Supported Format'];

    constructor(
        private store$: Store,
        private dialogRef: MatDialogRef<UploadFilesDialogComponent>,
        private _fileUpload: FileUpload,
        private _utils: UtilsService
    ) {}

    private getTransformations = () => {
        this.store$.dispatch(GetInstanceTransformations({ instanceId: this.record?.recordId, assetUid: this.metaData?.uid }));
    };

    closeModal = () => {
        this.dialogRef.close();
    };

    private getOrganizations = (key, relativeOrganizations) => {
        return relativeOrganizations[key];
    };

    public getSelectedTransformation = () => {
        this.transformationSources = [];
        if (this.selectedTransformationName && this.selectedTransformationName.length > 0) {
            for (let i = 0; i < this.transformations.length; i++) {
                for (let j = 0; j < this.transformations[i].transformations.length; j++) {
                    if (this.selectedTransformationName === this.transformations[i].transformations[j].id) {
                        this.selectedTransformation = this.transformations[i].transformations[j];
                        this.transformationSources = this.transformations[i].transformations[j].sourceParts;
                    }
                }
            }
        }
        this.buildTableData();
    };

    private buildTableData = () => {
        this.sourceTableData.next(this.transformationSources);
    };

    public downloadSourceTemplateUrl = data => {
        const url =
            `${environment['taxilla_api']}/v2/services/${encodeURIComponent(this.metaData.restAPIName)}/user-template` +
            `?transformationName=${encodeURIComponent(this.selectedTransformationName)}` +
            `&sourceName=${encodeURIComponent(data.name)}`;
        location.href = url;
    };

    public validateUploadProcessesData = () => {
        if (this.checkInputUploadData()) {
            this.uploadProcessesData();
        }
    };

    private checkInputUploadData = () => {
        if (this.selectedTransformationName && this.selectedTransformationName.length > 0) {
            const sources = this.sourceTableData.value;
            let count = 0;
            let noSourceFile = 0;
            sources.forEach(source => {
                const requiredFileFormats = source.fileExtension;
                const otherFormats = ['.zip'];
                const allFormats = [...otherFormats];
                if (source.fileExtension?.length > 0) {
                    allFormats.push(requiredFileFormats);
                }
                if (source.files) {
                    const givenFileFormat = source.files[0].name.substring(source.files[0].name.lastIndexOf('.'));
                    const fileFormat = givenFileFormat.toLowerCase();
                    if (allFormats.indexOf(fileFormat) === -1) {
                        count = count + 1;
                    }
                } else {
                    if (source.sourceDataFormat !== 'ASSET') {
                        noSourceFile = noSourceFile + 1;
                    }
                }
            });
            if (noSourceFile) {
                this.store$.dispatch(AlertError({ message: translate('Please upload a file') }));
                this.transformationErrors = [];
                return false;
            } else {
                if (count) {
                    this.store$.dispatch(AlertError({ message: translate('Please select valid file format') }));
                    this.transformationErrors = [];
                    return false;
                } else {
                    return true;
                }
            }
        } else {
            return true;
        }
    };

    private uploadProcessesData = () => {
        const formData = new FormData();
        const sources = this.sourceTableData.value;
        sources.forEach(source => {
            source.files &&
                source.files.forEach(file => {
                    formData.append(source.name, file);
                });
        });
        let url =
            environment.taxilla_api +
            '/process/v1/' +
            this.metaData.restAPIName +
            '?sourceType=UPLOAD&instanceId=' +
            this.record?.recordId +
            '&requestId=' +
            this.record?.requestId +
            '&transformationName=' +
            this.selectedTransformation.chainName;
        if (this.record?.bridge && this.record?.bridge.restApiName) {
            url += '&bridgeRestApiName=' + this.record?.bridge.restApiName;
        }
        this._fileUpload.uploadRequest(
            'PUT',
            url,
            formData,
            undefined,
            response => {
                this.store$.dispatch(AlertSuccess({ message: (response && response.msg) || translate('Files has been uploaded') }));
                this.store$.dispatch(ClearDownloadableFiles({ instanceId: this.record?.recordId }));
                this.closeModal();
            },
            response => {
                this.store$.dispatch(AlertError({ message: (response && response.msg) || translate('Failed to upload files') }));
                this._utils.hideLoading();
            }
        );
    };

    ngOnInit(): void {
        this.store$
            .select(getInstanceTransformations$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe((data: any) => {
                const allMapTransformations = CommonUtilsService.cloneObject(data?.[this.record?.recordId] || {});
                this.transformations = [];
                this.assetToAssetTransformations = [];
                const relativeOrganizations = allMapTransformations?.organizations;
                const organizations = allMapTransformations?.transformations;
                const nameVsDisplayNames = allMapTransformations?.chainNameVsChainDisplayName;
                if (organizations) {
                    for (const key in organizations) {
                        const transformationObject = {
                            orgName: this.getOrganizations(key, relativeOrganizations),
                            repositoryId: key,
                            transformations: [],
                            assetToAssetTransformations: [],
                        };
                        for (const transformationKey in organizations[key]) {
                            const transformation = new Transformation(
                                organizations[key][transformationKey],
                                nameVsDisplayNames[transformationKey]
                            );
                            transformation.chainName = transformationKey;
                            if (transformation.assetToAssetMetadata && Object.keys(transformation.assetToAssetMetadata).length > 0) {
                                transformationObject.assetToAssetTransformations.push(transformation);
                            } else {
                                transformationObject.transformations.push(transformation);
                            }
                        }
                        if (transformationObject.transformations.length > 0) {
                            const partitionTransformations = transformationObject.transformations.filter(transformation => {
                                return transformation.sourceParts[0].bulkUpload === false;
                            });
                            this.transformations.push({
                                orgName: transformationObject.orgName,
                                transformations: partitionTransformations,
                                repositoryId: transformationObject.repositoryId,
                            });
                        }
                        if (transformationObject.assetToAssetTransformations.length > 0) {
                            this.assetToAssetTransformations.push({
                                orgName: transformationObject.orgName,
                                transformations: transformationObject.assetToAssetTransformations,
                            });
                        }
                    }
                }
            });

        this.getTransformations();
    }

    ngOnDestroy(): void {
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }
}
